import { useEffect, useState, useCallback } from 'react';
import { getTelegram, isInTelegram, confirmDialog, alertDialog } from './telegram';
import { authTelegram, setToken, getMe, getClientMe, upsertCartItem, clearBackendCart, createOrder, checkTodayOrder, getPublicSettings, getDepositMe } from './api';
import { useCart } from './hooks/useCart';
import CatalogPage from './pages/CatalogPage';
import CartPage from './pages/CartPage';
import TopUpModal from './components/TopUpModal';
import RegistrationPage from './pages/RegistrationPage';
import PrivacyGatePage from './pages/PrivacyGatePage';
import EmailVerifyGatePage from './pages/EmailVerifyGatePage';
import WebAuth from './pages/WebAuth';
import ClientStatusPage from './pages/ClientStatusPage';
import CoffeeShopSelectPage from './pages/CoffeeShopSelectPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import OrderHistoryPage from './pages/OrderHistoryPage';
import DepositPage from './pages/DepositPage';
import FranchiseCabinetPage from './pages/FranchiseCabinetPage';
import NotificationsPage from './pages/NotificationsPage';
import type { CoffeeShop, OrderCreated, ClientInfo, ClientStatus, RepresentativeType, DepositAccount } from './types';
import './index.css';

type Page = 'catalog' | 'cart' | 'register' | 'client-status' | 'supplier-status' | 'staff-info' | 'shop-select' | 'order-success' | 'order-history' | 'deposit' | 'notifications';

export default function App() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<Page>('catalog');
  // Этап 17: boss и network_manager попадают в «Кабинет франшизы» вместо каталога.
  const [isFranchise, setIsFranchise] = useState(false);
  // Этап 19.5: точка, для которой network_manager оформляет заказ. Если задана —
  // показываем каталог/корзину для неё; иначе — обычный кабинет франшизы.
  const [franchiseShop, setFranchiseShop] = useState<CoffeeShop | null>(null);
  // Этап 20: статус заявки представителя поставщика (для экрана 'supplier-status').
  const [supplierStatus, setSupplierStatus] = useState<ClientStatus | null>(null);
  // Этап 30: «турникет» — существующему пользователю нужно заново согласиться с
  // обновлённой Политикой ПДн. «Свежим» (идут на регистрацию) не показываем.
  const [mustAcceptPrivacy, setMustAcceptPrivacy] = useState(false);
  // Этап W1 (вариант B): «турникет» подтверждения e-mail. Включается для
  // самостоятельно зарегистрированного клиента, пока почта не подтверждена.
  const [mustVerifyEmail, setMustVerifyEmail] = useState(false);
  const [verifyEmailAddr, setVerifyEmailAddr] = useState<string | null>(null);

  const [checkoutLoading, setCheckoutLoading] = useState(false);
  // Минимальная сумма заказа (руб.). 0 — ограничение выключено / ещё не загружено.
  const [minOrderAmount, setMinOrderAmount] = useState(0);
  const [coffeeShops, setCoffeeShops] = useState<CoffeeShop[]>([]);
  const [selectedClientInfo, setSelectedClientInfo] = useState<ClientInfo | null>(null);
  const [createdOrder, setCreatedOrder] = useState<OrderCreated | null>(null);

  const { items, add, dec, getQty, totalItems, totalPrice, clear } = useCart();

  // Блок B: депозиты моих кофеен (остаток/резерв/доступно) и окно пополнения.
  const [deposits, setDeposits] = useState<DepositAccount[]>([]);
  const [depositLoading, setDepositLoading] = useState(false);
  const [depositsLoaded, setDepositsLoaded] = useState(false);
  const [topUpOpen, setTopUpOpen] = useState(false);
  // Блок D: точка, для которой открыто окно пополнения со страницы «Депозит».
  const [topUpShopId, setTopUpShopId] = useState<string | null>(null);
  const loadDeposits = useCallback(async () => {
    setDepositLoading(true);
    try {
      const { data } = await getDepositMe();
      setDeposits(data);
    } catch {
      /* не критично — гейт проверит бэкенд при оформлении */
    } finally {
      setDepositLoading(false);
      setDepositsLoaded(true);
    }
  }, []);
  // Блок D: остаток нужен уже в шапке каталога, на странице «Депозит» и в корзине.
  // Вне Telegram депозиты грузит WebShop; в Telegram ждём окончания авторизации
  // (иначе первый запрос уйдёт без токена и получит 403).
  useEffect(() => {
    if (!isInTelegram() || loading) return;
    if (page === 'catalog' || page === 'cart' || page === 'deposit') { loadDeposits(); }
  }, [page, loading, loadDeposits]);
  const openTopUp = useCallback(() => {
    if (deposits.length === 0) {
      alertDialog('Не найдена кофейня для пополнения депозита. Обратитесь к менеджеру.');
      return;
    }
    setTopUpOpen(true);
  }, [deposits.length]);
  const openTopUpFor = useCallback((shopId: string) => { setTopUpShopId(shopId); setTopUpOpen(true); }, []);
  const closeTopUp = useCallback(() => { setTopUpOpen(false); setTopUpShopId(null); loadDeposits(); }, [loadDeposits]);

  const goCart = useCallback(() => setPage('cart'), []);
  const goCatalog = useCallback(() => { setPage('catalog'); }, []);
  const goHistory = useCallback(() => setPage('order-history'), []);
  const goDeposit = useCallback(() => setPage('deposit'), []);
  // Блок «Уведомления в приложении»: экран уведомлений и переход к объекту из него.
  const goNotifications = useCallback(() => setPage('notifications'), []);
  const openNotificationEntity = useCallback((entityType: string | null) => {
    if (entityType === 'order') setPage('order-history');
    else if (entityType === 'deposit') setPage('deposit');
    else setPage('catalog');
  }, []);

  // Этап 19.5: network_manager выбрал точку в кабинете → открываем для неё каталог.
  // Локальную корзину очищаем, чтобы не смешать товары между точками сети.
  const goOrderForShop = useCallback((shop: CoffeeShop) => {
    clear();
    setFranchiseShop(shop);
    setPage('catalog');
  }, [clear]);

  // Этап 19.5: вернуться из режима заказа франшизы обратно в кабинет.
  const goBackToCabinet = useCallback(() => {
    clear();
    setFranchiseShop(null);
    setPage('catalog');
  }, [clear]);

  const placeOrder = useCallback(async (shop: CoffeeShop) => {
    setCheckoutLoading(true);
    try {
      // Этап 22: мягкое предупреждение о повторном заказе на ту же точку
      // в текущий операционный день. Не блокируем жёстко — спрашиваем подтверждение.
      const { data: chk } = await checkTodayOrder(shop.id);
      if (chk.has_order_today) {
        const ok = await confirmDialog(
          `Сегодня на «${shop.name}» уже оформлен заказ ${chk.public_number ?? ''}. Создать ещё один?`,
        );
        if (!ok) { setPage('cart'); return; }
      }
      await clearBackendCart(shop.id);
      for (const item of items) {
        await upsertCartItem(shop.id, item.product.id, item.quantity);
      }
      const { data } = await createOrder(shop.id);
      clear();
      setCreatedOrder(data);
      setPage('order-success');
    } catch (e) {
      alert(`Ошибка оформления: ${(e as Error).message}`);
      setPage('cart');
      loadDeposits();
    } finally {
      setCheckoutLoading(false);
    }
  }, [items, clear, loadDeposits]);

  // Этап 19.5: оформление для NM — точка уже выбрана в кабинете, поэтому минуем
  // клиентский путь getClientMe и сразу размещаем заказ (с проверкой дубля внутри).
  const goFranchiseCheckout = useCallback(async () => {
    if (checkoutLoading || !franchiseShop) return;
    await placeOrder(franchiseShop);
  }, [checkoutLoading, franchiseShop, placeOrder]);

  const goCheckout = useCallback(async () => {
    if (checkoutLoading) return;
    setCheckoutLoading(true);
    try {
      const { data: clientData } = await getClientMe();
      setSelectedClientInfo(clientData.client);
      if (clientData.client.status !== 'approved') {
        setPage('client-status');
        return;
      }
      const activeShops = clientData.coffee_shops.filter(s => s.status === 'active');
      if (activeShops.length === 0) {
        setPage('client-status');
        return;
      }
      if (activeShops.length === 1) {
        await placeOrder(activeShops[0]);
      } else {
        setCoffeeShops(activeShops);
        setPage('shop-select');
      }
    } catch (e) {
      const msg = (e as Error).message;
      if (msg.startsWith('404')) {
        setPage('register');
      } else {
        alert(`Ошибка: ${msg}`);
      }
    } finally {
      setCheckoutLoading(false);
    }
  }, [checkoutLoading, placeOrder]);

  useEffect(() => {
    const tg = getTelegram();
    tg?.ready();
    tg?.expand();

    if (!isInTelegram()) {
      setLoading(false);
      return;
    }

    const initData = window.Telegram!.WebApp!.initData;
    authTelegram(initData)
      .then(async ({ data }) => {
        setToken(data.access_token);
        // Тянем минимальную сумму заказа (не критично — при ошибке остаётся 0 = без лимита).
        getPublicSettings()
          .then(({ data: s }) => setMinOrderAmount(Number(s.min_order_amount) || 0))
          .catch(() => { /* не блокируем загрузку приложения */ });
        // Этап 20: единая маршрутизация при первом открытии.
        //  1) boss / network_manager → «Кабинет франшизы» (не каталог);
        //  2) «свежий» пользователь (нет ролей, клиента и заявки) → форма регистрации;
        //  3) представитель поставщика (есть заявка supplier, клиента нет) → экран статуса;
        //  4) остальные (клиент GreenHouse / staff) → каталог по умолчанию.
        try {
          const { data: me } = await getMe();
          const roles = me.roles ?? [];
          const hasClient = !!me.client_status;
          const reg = me.registration;
          // Этап 30: «турникет». «Свежий» пользователь (нет клиента, заявки и ролей)
          // примет согласие в форме регистрации — ему гейт не показываем. Всем
          // остальным при смене версии Политики показываем блокирующий экран.
          const isFresh = !hasClient && !reg && roles.length === 0;
          if (!isFresh && me.privacy?.needs_acceptance) {
            setMustAcceptPrivacy(true);
          }
          // Этап W1: жёсткий гейтинг почты — только для самостоятельно
          // зарегистрированного клиента (есть e-mail, но не подтверждён, и это
          // НЕ franchise/staff). Franchise/staff заводит Админ — их не блокируем.
          const isFranchiseUser = roles.includes('boss') || roles.includes('network_manager');
          const hasStaffRole = roles.some(r => r !== 'client');
          if (!isFresh && me.email && !me.email_verified && !isFranchiseUser && !hasStaffRole) {
            setVerifyEmailAddr(me.email);
            setMustVerifyEmail(true);
          }
          if (isFranchiseUser) {
            setIsFranchise(true);
            return;
          }
          if (isFresh) {
            setPage('register');
            return;
          }
          // Этап 20: сотрудник (есть staff-роль, нет клиента) — например, поставщик,
          // которому Админ уже выдал роль. Каталог ему не нужен (работает в веб-панели).
          // Проверяем ДО supplier-status, т.к. у такого юзера есть и заявка, и роль.
          if (!hasClient && hasStaffRole) {
            setPage('staff-info');
            return;
          }
          if (reg && reg.type === 'supplier' && !hasClient) {
            setSupplierStatus(reg.status as ClientStatus);
            setPage('supplier-status');
            return;
          }
        } catch {
          // /me не критичен — при ошибке просто покажем обычный каталог.
        }
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <span className="hint">Загрузка…</span>
      </div>
    );
  }

  // Этап W1 (вариант B): вне Telegram (обычный браузер) показываем веб-вход и
  // веб-регистрацию — резервный канал на случай блокировки Telegram.
  if (!isInTelegram()) {
    return <WebAuth />;
  }

  if (error) {
    return (
      <div className="container" style={{ padding: '32px 16px' }}>
        <p className="hint">Ошибка авторизации: {error}</p>
      </div>
    );
  }

  // Этап 30: «турникет» Политики ПДн имеет приоритет над всеми экранами (кроме
  // загрузки/ошибки). После подтверждения флаг снимается и пользователь попадает
  // на свой обычный экран (каталог/кабинет/статус), маршрут которого уже выбран.
  // Этап W1: «турникет» подтверждения e-mail имеет наивысший приоритет среди
  // блокирующих экранов — без подтверждённой почты в сервис не пускаем.
  if (mustVerifyEmail) {
    return <EmailVerifyGatePage email={verifyEmailAddr} onVerified={() => setMustVerifyEmail(false)} />;
  }

  if (mustAcceptPrivacy) {
    return <PrivacyGatePage onAccepted={() => setMustAcceptPrivacy(false)} />;
  }

  // Блок B: депозит точки, для которой оформляется заказ. Для NM — выбранная в
  // кабинете точка; для клиента — единственная кофейня (при нескольких точку
  // выберут при оформлении, и гейт проверит бэкенд).
  const activeDeposit: DepositAccount | null = franchiseShop
    ? (deposits.find(d => d.coffee_shop_id === franchiseShop.id) ?? null)
    : (deposits.length === 1 ? deposits[0] : null);
  const topUpModal = topUpOpen && deposits.length > 0
    ? <TopUpModal shops={deposits} initialShopId={topUpShopId ?? activeDeposit?.coffee_shop_id ?? null} onClose={closeTopUp} />
    : null;

  // Этап 17: boss и network_manager видят «Кабинет франшизы» вместо каталога/корзины.
  // Этап 19.5: network_manager может из кабинета выбрать точку и оформить для неё заказ.
  if (isFranchise) {
    if (!franchiseShop) {
      // Блок D: страница «Депозит» из кабинета — все точки, доступные boss / NM.
      if (page === 'deposit') {
        return (
          <>
            <DepositPage onBack={goCatalog} backLabel="‹ В кабинет" deposits={deposits} loading={depositLoading} loaded={depositsLoaded} onTopUp={openTopUpFor} />
            {topUpModal}
          </>
        );
      }
      if (page === 'notifications') {
        return <NotificationsPage onBack={goCatalog} backLabel="‹ В кабинет" onOpenEntity={openNotificationEntity} />;
      }
      return <FranchiseCabinetPage onOrderForShop={goOrderForShop} onDeposits={goDeposit} onNotifications={goNotifications} />;
    }
    // Режим заказа франшизы: каталог/корзина/успех для выбранной точки.
    if (page === 'order-success' && createdOrder) {
      return <OrderSuccessPage order={createdOrder} onHistory={goHistory} onContinue={goBackToCabinet} />;
    }
    if (page === 'notifications') return <NotificationsPage onBack={goCatalog} backLabel="‹ В каталог" onOpenEntity={openNotificationEntity} />;
    if (page === 'order-history') return <OrderHistoryPage onBack={goCatalog} />;
    if (page === 'deposit') {
      return (
        <>
          <DepositPage onBack={goCatalog} backLabel="‹ В каталог" deposits={deposits} loading={depositLoading} loaded={depositsLoaded} onTopUp={openTopUpFor} initialShopId={franchiseShop.id} />
          {topUpModal}
        </>
      );
    }
    if (page === 'cart') {
      return (
        <>
          <CartPage onBack={goCatalog} onCheckout={goFranchiseCheckout} checkoutLoading={checkoutLoading} items={items} add={add} dec={dec} totalItems={totalItems} totalPrice={totalPrice} minOrderAmount={minOrderAmount} deposit={activeDeposit} depositLoading={depositLoading} onTopUp={openTopUp} onDeposit={goDeposit} />
          {topUpModal}
        </>
      );
    }
    return <CatalogPage onCartOpen={goCart} onHistory={goHistory} add={add} dec={dec} getQty={getQty} totalItems={totalItems} totalPrice={totalPrice} contextShop={franchiseShop} onBackToCabinet={goBackToCabinet} onDeposit={goDeposit} depositAvailable={activeDeposit?.available ?? null} onNotifications={goNotifications} />;
  }

  if (page === 'deposit') {
    return (
      <>
        <DepositPage onBack={goCatalog} backLabel="‹ В каталог" deposits={deposits} loading={depositLoading} loaded={depositsLoaded} onTopUp={openTopUpFor} />
        {topUpModal}
      </>
    );
  }
  if (page === 'notifications') return <NotificationsPage onBack={goCatalog} backLabel="‹ В каталог" onOpenEntity={openNotificationEntity} />;
  if (page === 'order-history') return <OrderHistoryPage onBack={goCatalog} />;
  if (page === 'order-success' && createdOrder) return <OrderSuccessPage order={createdOrder} onHistory={goHistory} onContinue={goCatalog} />;
  if (page === 'shop-select') return <CoffeeShopSelectPage shops={coffeeShops} onSelect={placeOrder} onBack={goCart} loading={checkoutLoading} />;
  if (page === 'client-status') return <ClientStatusPage info={selectedClientInfo} onBack={goCatalog} />;
  if (page === 'supplier-status') return <ClientStatusPage kind="supplier" info={supplierStatus ? { id: '', status: supplierStatus, rejection_comment: null, approved_at: null } : null} onBack={goCatalog} />;
  if (page === 'staff-info') return <ClientStatusPage kind="staff" info={null} onBack={goCatalog} />;
  if (page === 'register') return <RegistrationPage onSuccess={(type: RepresentativeType) => {
    if (type === 'supplier') { setSupplierStatus('pending'); setPage('supplier-status'); }
    else { setSelectedClientInfo({ id: '', status: 'pending', rejection_comment: null, approved_at: null }); setPage('client-status'); }
  }} onBack={goCatalog} />;
  if (page === 'cart') {
    return (
      <>
        <CartPage onBack={goCatalog} onCheckout={goCheckout} checkoutLoading={checkoutLoading} items={items} add={add} dec={dec} totalItems={totalItems} totalPrice={totalPrice} minOrderAmount={minOrderAmount} deposit={activeDeposit} depositLoading={depositLoading} onTopUp={openTopUp} onDeposit={goDeposit} />
        {topUpModal}
      </>
    );
  }
  return <CatalogPage onCartOpen={goCart} onHistory={goHistory} add={add} dec={dec} getQty={getQty} totalItems={totalItems} totalPrice={totalPrice} onDeposit={goDeposit} depositAvailable={activeDeposit?.available ?? null} onNotifications={goNotifications} />;
}
